import { OUTBOX_STORE, runRequest } from './offline-db';
import { newIdempotencyKey, randomId } from './submission-headers';
import type { NewOutboxItem, OutboxItem, OutboxKind } from './outbox.types';

/**
 * Contributions waiting on this device, per account. Every read is scoped to
 * one owner: another account signed in on the same browser never sees nor
 * sends them.
 */

/** Fired on window after any change, in this tab and (via BroadcastChannel) the others. */
export const OUTBOX_CHANGED_EVENT = 'yodi:outbox-changed';
const CHANNEL_NAME = 'yodi-outbox';

let channel: BroadcastChannel | null = null;
function outboxChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = () => window.dispatchEvent(new CustomEvent(OUTBOX_CHANGED_EVENT));
  }
  return channel;
}

export function notifyOutboxChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OUTBOX_CHANGED_EVENT));
  try {
    outboxChannel()?.postMessage('changed');
  } catch {
    // Another tab simply refreshes later.
  }
}

/** Starts listening to the other tabs (the channel is opened lazily). */
export function listenToOtherTabs(): void {
  outboxChannel();
}

const byCreation = (a: OutboxItem, b: OutboxItem) =>
  a.createdAt === b.createdAt ? a.id.localeCompare(b.id) : a.createdAt.localeCompare(b.createdAt);

export const outboxStore = {
  async enqueue<K extends OutboxKind>(input: NewOutboxItem<K>): Promise<OutboxItem<K>> {
    const item: OutboxItem<K> = {
      id: randomId(),
      ownerId: String(input.ownerId),
      kind: input.kind,
      payload: input.payload,
      summary: input.summary,
      idempotencyKey: input.idempotencyKey ?? newIdempotencyKey(),
      createdAt: input.createdAt ?? new Date().toISOString(),
      attempts: 0,
      status: 'pending',
    };
    await runRequest(OUTBOX_STORE, 'readwrite', (store) => store.add(item));
    notifyOutboxChanged();
    return item;
  },

  /** The owner's items, oldest first: the order they are sent in. */
  async list(ownerId: string): Promise<OutboxItem[]> {
    const items = await runRequest<OutboxItem[]>(OUTBOX_STORE, 'readonly', (store) =>
      store.index('ownerId').getAll(IDBKeyRange.only(String(ownerId))),
    );
    return items.sort(byCreation);
  },

  async get(ownerId: string, id: string): Promise<OutboxItem | undefined> {
    const item = await runRequest<OutboxItem | undefined>(OUTBOX_STORE, 'readonly', (store) => store.get(id));
    return item && item.ownerId === String(ownerId) ? item : undefined;
  },

  async update(item: OutboxItem): Promise<void> {
    await runRequest(OUTBOX_STORE, 'readwrite', (store) => store.put(item));
    notifyOutboxChanged();
  },

  /** Removes one of the owner's items; an id of another account is ignored. */
  async remove(ownerId: string, id: string): Promise<void> {
    const item = await outboxStore.get(ownerId, id);
    if (!item) return;
    await runRequest(OUTBOX_STORE, 'readwrite', (store) => store.delete(id));
    notifyOutboxChanged();
  },

  /** A failed item goes back in the queue, keeping its idempotency key. */
  async retry(ownerId: string, id: string): Promise<void> {
    const item = await outboxStore.get(ownerId, id);
    if (!item || item.status !== 'failed') return;
    await outboxStore.update({ ...item, status: 'pending' });
  },
};

export type OutboxStore = typeof outboxStore;
